import {
  BillLine,
  BillingConfig,
  CatalogueItem,
  DisplayStatus,
  SalesDocument,
  TaxRate,
} from '../types/billing'

/**
 * Decimal-safe billing arithmetic. Every amount is converted to whole ngwee
 * (1/100 kwacha) before calculation and rounded half-up, so totals never drift.
 */
export const toN = (kwacha: number) => Math.round((Number(kwacha) || 0) * 100 + Number.EPSILON * 100)
export const fromN = (ngwee: number) => Math.round(ngwee) / 100
export const addK = (...values: number[]) => fromN(values.reduce((sum, v) => sum + toN(v), 0))
export const subK = (a: number, b: number) => fromN(toN(a) - toN(b))
export const minK = (a: number, b: number) => (toN(a) <= toN(b) ? a : b)
export const isZero = (k: number) => toN(k) === 0
export const gtK = (a: number, b: number) => toN(a) > toN(b)

export function formatMoney(value: number, symbol = 'K') {
  const n = toN(value)
  const abs = Math.abs(n) / 100
  return `${n < 0 ? '−' : ''}${symbol} ${abs.toLocaleString('en-ZM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export const makeId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

export const newClientKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : makeId('ck')

/** Today's calendar date in Africa/Lusaka (yyyy-mm-dd), independent of the device time zone. */
export function todayLusaka(at = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Lusaka', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '01'
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function addDays(iso: string, days: number) {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function daysBetween(fromIso: string, toIso: string) {
  return Math.round((new Date(`${toIso}T00:00:00`).getTime() - new Date(`${fromIso}T00:00:00`).getTime()) / 86400000)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function formatBillingDate(iso: string | undefined, format: BillingConfig['regional']['dateFormat'] = 'dd/MM/yyyy') {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  if (!y || !m || !d) return iso
  if (format === 'yyyy-MM-dd') return `${y}-${m}-${d}`
  if (format === 'dd MMM yyyy') return `${d} ${MONTHS[Number(m) - 1]} ${y}`
  return `${d}/${m}/${y}`
}

export function formatDateTime(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', { timeZone: 'Africa/Lusaka', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export type RangePreset = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom'
export const rangePresets: { id: RangePreset; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'This quarter' },
  { id: 'year', label: 'This financial year' },
  { id: 'custom', label: 'Custom' },
]
export function rangeFor(preset: RangePreset, today = todayLusaka(), opts: { weekStart?: 'Monday' | 'Sunday'; fyStartMonth?: number } = {}): { from: string; to: string } {
  const [y, m] = today.split('-').map(Number)
  if (preset === 'today') return { from: today, to: today }
  if (preset === 'week') return { from: weekRange(today, opts.weekStart).from, to: today }
  if (preset === 'year' && opts.fyStartMonth && opts.fyStartMonth > 1) {
    const startYear = m >= opts.fyStartMonth ? y : y - 1
    return { from: `${startYear}-${String(opts.fyStartMonth).padStart(2, '0')}-01`, to: today }
  }
  if (preset === 'quarter') {
    const q = Math.floor((m - 1) / 3) * 3 + 1
    return { from: `${y}-${String(q).padStart(2, '0')}-01`, to: today }
  }
  if (preset === 'year') return { from: `${y}-01-01`, to: today }
  return { from: `${y}-${String(m).padStart(2, '0')}-01`, to: today }
}

// ─── Line and document calculation ───

export type LineInput = Omit<BillLine, 'gross' | 'discount' | 'tax' | 'total'> &
  Partial<Pick<BillLine, 'gross' | 'discount' | 'tax' | 'total'>>

export function calculateLine(line: LineInput): BillLine {
  if (line.fixed) {
    return { ...line, gross: line.gross ?? 0, discount: line.discount ?? 0, tax: line.tax ?? 0, total: line.total ?? 0 }
  }
  const qty = Math.max(0, Number(line.quantity) || 0)
  const pct = Math.min(100, Math.max(0, Number(line.discountPct) || 0))
  const rate = Math.max(0, Number(line.taxRate) || 0)
  const grossN = Math.round(qty * toN(line.unitPrice))
  const discountN = Math.round((grossN * pct) / 100)
  const afterN = grossN - discountN
  const taxN = line.taxInclusive ? Math.round((afterN * rate) / (100 + rate)) : Math.round((afterN * rate) / 100)
  const totalN = line.taxInclusive ? afterN : afterN + taxN
  return { ...line, quantity: qty, discountPct: pct, taxRate: rate, gross: fromN(grossN), discount: fromN(discountN), tax: fromN(taxN), total: fromN(totalN) }
}

export function documentTotals(lines: BillLine[]) {
  const sum = (key: 'gross' | 'discount' | 'tax' | 'total') => fromN(lines.reduce((s, l) => s + toN(l[key]), 0))
  return { subtotal: sum('gross'), discountTotal: sum('discount'), taxTotal: sum('tax'), total: sum('total') }
}

/** Resolves a tax code. Inactive codes or codes not yet effective fall back to the default, then Exempt. */
export function taxFor(config: BillingConfig, taxId?: string, on = todayLusaka()): TaxRate {
  const usable = (t?: TaxRate) => (t && t.active && (!t.effectiveFrom || t.effectiveFrom <= on) ? t : undefined)
  return (
    usable(config.taxes.rates.find((t) => t.id === taxId)) ??
    usable(config.taxes.rates.find((t) => t.id === config.taxes.defaultTaxId)) ??
    config.taxes.rates.find((t) => t.id === 'exempt') ?? { id: 'exempt', code: 'E', name: 'Exempt', rate: 0, classification: 'Exempt', active: true }
  )
}

/** Tax fields copied onto a line at the moment it is priced — historical lines never re-read settings. */
export function taxFields(tax: TaxRate) {
  return { taxId: tax.id, taxCode: tax.code ?? tax.id.toUpperCase(), taxName: tax.name, taxRate: tax.rate, taxClass: tax.classification ?? (tax.rate > 0 ? 'Standard' : 'Exempt') } as const
}

export function lineFromItem(config: BillingConfig, item: CatalogueItem, extra: Partial<BillLine> = {}): BillLine {
  const tax = taxFor(config, item.taxId)
  return calculateLine({
    id: makeId('ln'),
    itemId: item.id,
    code: item.code,
    description: item.name,
    department: item.department,
    unit: item.unit,
    quantity: 1,
    unitPrice: item.unitPrice,
    discountPct: 0,
    ...taxFields(tax),
    taxInclusive: item.taxInclusive ?? config.taxes.pricesIncludeTax,
    ...extra,
  })
}

export function blankLine(config: BillingConfig): BillLine {
  const tax = taxFor(config)
  return calculateLine({
    id: makeId('ln'), description: '', quantity: 1, unitPrice: 0, discountPct: 0,
    ...taxFields(tax), taxInclusive: config.taxes.pricesIncludeTax,
  })
}

/** Tax classification of a stored line (older lines without a class are inferred from the rate). */
export const lineTaxClass = (line: BillLine) => line.taxClass ?? (line.taxRate > 0 ? 'Standard' : 'Exempt')

/** Week containing `day`, honouring the configured first day of the week. */
export function weekRange(day: string, weekStart: 'Monday' | 'Sunday' = 'Monday') {
  const d = new Date(`${day}T00:00:00`)
  const offset = weekStart === 'Monday' ? (d.getDay() + 6) % 7 : d.getDay()
  const from = addDays(day, -offset)
  return { from, to: addDays(from, 6) }
}

export function monthRange(year: number, month: number) {
  const last = new Date(year, month, 0).getDate()
  const m = String(month).padStart(2, '0')
  return { from: `${year}-${m}-01`, to: `${year}-${m}-${String(last).padStart(2, '0')}` }
}

/** Splits lines between a payer (percent) and the patient (remainder) to the ngwee — totals always add back up. */
export function splitLines(lines: BillLine[], payerPercent: number): [BillLine[], BillLine[]] {
  const payer: BillLine[] = []
  const patient: BillLine[] = []
  for (const line of lines) {
    const share = (v: number) => Math.round((toN(v) * payerPercent) / 100)
    const p = { gross: share(line.gross), discount: share(line.discount), tax: share(line.tax), total: share(line.total) }
    const base = { ...line, fixed: true }
    payer.push({ ...base, id: `${line.id}-p`, description: `${line.description} (${payerPercent}% cover)`, gross: fromN(p.gross), discount: fromN(p.discount), tax: fromN(p.tax), total: fromN(p.total) })
    patient.push({
      ...base, id: `${line.id}-c`, description: `${line.description} (${100 - payerPercent}% patient share)`,
      gross: fromN(toN(line.gross) - p.gross), discount: fromN(toN(line.discount) - p.discount), tax: fromN(toN(line.tax) - p.tax), total: fromN(toN(line.total) - p.total),
    })
  }
  return [payer, patient]
}

// ─── Status ───

export function displayStatus(doc: SalesDocument, today = todayLusaka()): DisplayStatus {
  if (doc.type === 'quotation') return doc.state as DisplayStatus
  if (doc.type === 'order') return doc.state as DisplayStatus
  if (doc.state === 'Draft') return 'Draft'
  if (doc.state === 'Void') return 'Void'
  if (toN(doc.balance) <= 0) {
    return toN(doc.amountCredited) > 0 && toN(doc.amountPaid) === 0 ? 'Credited' : 'Paid'
  }
  if (doc.dueDate && doc.dueDate < today) return 'Overdue'
  if (toN(doc.amountPaid) + toN(doc.amountCredited) > 0) return 'Partially Paid'
  return 'Unpaid'
}

export const statusTone: Record<DisplayStatus, string> = {
  Draft: 'bg-slate-100 text-slate-700 ring-slate-200',
  Unpaid: 'bg-amber-50 text-amber-800 ring-amber-200',
  'Partially Paid': 'bg-sky-50 text-sky-800 ring-sky-200',
  Paid: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  Credited: 'bg-violet-50 text-violet-800 ring-violet-200',
  Overdue: 'bg-red-50 text-red-800 ring-red-200',
  Void: 'bg-slate-100 text-slate-500 ring-slate-200 line-through',
  Sent: 'bg-sky-50 text-sky-800 ring-sky-200',
  Accepted: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  Declined: 'bg-red-50 text-red-700 ring-red-200',
  Converted: 'bg-violet-50 text-violet-800 ring-violet-200',
  Open: 'bg-amber-50 text-amber-800 ring-amber-200',
  Cancelled: 'bg-slate-100 text-slate-500 ring-slate-200',
}

export const docLabel = (type: SalesDocument['type']) =>
  type === 'invoice' ? 'Invoice' : type === 'quotation' ? 'Quotation' : 'Order'

export const docRef = (doc: Pick<SalesDocument, 'number' | 'type'>) => doc.number ?? `Draft ${docLabel(doc.type).toLowerCase()}`

/** Outstanding invoices count toward receivables only when issued and not void. */
export const isReceivable = (doc: SalesDocument) => doc.type === 'invoice' && doc.state === 'Issued'

export function ageingBucket(doc: SalesDocument, buckets: number[], today = todayLusaka()) {
  const overdue = doc.dueDate ? daysBetween(doc.dueDate, today) : daysBetween(doc.date, today)
  if (overdue <= 0) return 'Current'
  const sorted = [...buckets].sort((a, b) => a - b)
  let lower = 1
  for (const b of sorted) {
    if (overdue <= b) return `${lower}–${b} days`
    lower = b + 1
  }
  return `${sorted[sorted.length - 1] ?? 90}+ days`
}

export function ageingLabels(buckets: number[]) {
  const sorted = [...buckets].sort((a, b) => a - b)
  const labels = ['Current']
  let lower = 1
  for (const b of sorted) {
    labels.push(`${lower}–${b} days`)
    lower = b + 1
  }
  labels.push(`${sorted[sorted.length - 1] ?? 90}+ days`)
  return labels
}
